/**
 * Routine Selection Modal
 * Modal for selecting existing routines from Hevy to add to a day
 */

import { Search, Loader2, Folder, FolderOpen } from 'lucide-react'
import { useMemo, useState } from 'react'
import { fuzzyFilterAndSort } from '../../lib/fuzzy-search'
import type { Routine } from '../../types'
import {
  Modal,
  ModalContent,
  ModalDescription,
  ModalHeader,
  ModalTitle
} from '../ui/modal'
import { Input } from '../ui/input'
import { useSplitConfigStore } from '../../stores/splitConfigStore'
import { useSplitUIStore } from '../../stores/splitUIStore'
import { DAY_DISPLAY_NAMES } from '../../lib/constants'
import { useRoutines, useRoutineFolders } from '../../api/hooks'

export const RoutineSelectionModal = () => {
  const { 
    createRoutineFromHevy,
    assignRoutineToDay,
    assignRoutineToCycleDay,
    cycleMode
  } = useSplitConfigStore()
  
  const { 
    isRoutineModalOpen, 
    selectedDay,
    selectedCycleDayIndex, 
    closeRoutineModal
  } = useSplitUIStore()
  
  const { data: routines = [], isLoading, error } = useRoutines()
  const { data: folders = [], isLoading: foldersLoading } = useRoutineFolders()
  const [searchTerm, setSearchTerm] = useState('')
  const [expandedFolders, setExpandedFolders] = useState<Set<number>>(new Set())

  // Organize routines by folders and filter based on search term
  const organizedData = useMemo(() => {
    // First filter all routines by search term
    const filteredRoutines = fuzzyFilterAndSort(routines, searchTerm, (routine) => routine.title)
    
    // Separate routines with and without folders
    const routinesWithoutFolder = filteredRoutines.filter(r => !r.folder_id)
    const routinesByFolder = folders.reduce((acc, folder) => {
      const folderRoutines = filteredRoutines.filter(r => r.folder_id === folder.id)
      if (folderRoutines.length > 0) {
        acc[folder.id] = folderRoutines
      }
      return acc
    }, {} as Record<number, Routine[]>)

    return { routinesWithoutFolder, routinesByFolder }
  }, [routines, folders, searchTerm])

  const toggleFolder = (folderId: number) => {
    setExpandedFolders(prev => {
      const newSet = new Set(prev)
      if (newSet.has(folderId)) {
        newSet.delete(folderId)
      } else {
        newSet.add(folderId)
      }
      return newSet
    })
  }

  // Add routine to split
  const handleSelectRoutine = (routine: Routine) => {
    // Create a new routine in our store from the Hevy routine
    const routineId = createRoutineFromHevy(routine)
    
    // Assign it to the appropriate day/cycle day
    if (cycleMode && selectedCycleDayIndex !== null) {
      assignRoutineToCycleDay(selectedCycleDayIndex, routineId)
    } else if (selectedDay) {
      assignRoutineToDay(selectedDay, routineId)
    }
    
    // Close the modal
    closeRoutineModal()
    setSearchTerm('') // Reset search
  }

  // Reset search when modal closes
  const handleClose = () => {
    setSearchTerm('')
    closeRoutineModal()
  }

  return (
    <Modal open={isRoutineModalOpen} onOpenChange={(open) => !open && handleClose()} modalId="routineSelection">
      <ModalContent className="max-w-2xl max-h-[80vh] flex flex-col">
        <ModalHeader>
          <ModalTitle>
            Select Routine for {cycleMode && selectedCycleDayIndex !== null 
              ? `Day ${selectedCycleDayIndex + 1}` 
              : selectedDay ? DAY_DISPLAY_NAMES[selectedDay] : ''}
          </ModalTitle>
          <ModalDescription>
            Choose an existing routine from your Hevy account.
            {routines.length > 0 && (
              <span className="block mt-1 text-xs">
                {routines.length} routines available
              </span>
            )}
          </ModalDescription>
        </ModalHeader>

        {/* Search Input */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search routines..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            autoFocus
          />
        </div>

        {/* Routine List */}
        <div className="flex-1 overflow-hidden">
          {isLoading || foldersLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin" />
              <span className="ml-2">Loading routines...</span>
            </div>
          ) : error ? (
            <div className="text-destructive text-center py-8">
              <p>Failed to load routines from Hevy</p>
              <p className="text-sm text-muted-foreground mt-1">
                Please check your API key configuration
              </p>
            </div>
          ) : routines.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-muted-foreground">No routines found</p>
              <p className="text-sm text-muted-foreground mt-1">
                Create routines in Hevy first to use them here
              </p>
            </div>
          ) : (
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {organizedData.routinesWithoutFolder.length === 0 && 
               Object.keys(organizedData.routinesByFolder).length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <p>No routines found</p>
                  {searchTerm && (
                    <p className="text-sm mt-1">Try a different search term</p>
                  )}
                </div>
              ) : (
                <>
                  {/* Folders with routines */}
                  {folders
                    .filter(folder => organizedData.routinesByFolder[folder.id]?.length > 0)
                    .map((folder) => {
                      const isExpanded = expandedFolders.has(folder.id)
                      const folderRoutines = organizedData.routinesByFolder[folder.id] || []

                      return (
                        <div key={folder.id}>
                          {/* Folder Header */}
                          <div
                            onClick={() => toggleFolder(folder.id)}
                            className="flex items-center gap-2 p-3 rounded-lg border cursor-pointer hover:bg-muted/50 transition-colors"
                          >
                            {isExpanded ? (
                              <FolderOpen className="h-4 w-4" />
                            ) : (
                              <Folder className="h-4 w-4" />
                            )}
                            <span className="text-muted-foreground font-medium">
                              {folder.title}
                            </span>
                            <span className="text-xs text-muted-foreground ml-auto">
                              {folderRoutines.length} routine{folderRoutines.length !== 1 ? 's' : ''}
                            </span>
                          </div>

                          {/* Folder Routines */}
                          {isExpanded && (
                            <div className="ml-6 space-y-2 mt-2">
                              {folderRoutines.map((routine) => (
                                <div
                                  key={routine.id}
                                  onClick={() => handleSelectRoutine(routine)}
                                  className="flex items-center rounded-lg border p-3 hover:bg-muted/50 transition-colors cursor-pointer"
                                >
                                  <div className="flex-1 min-w-0">
                                    <div className="font-medium truncate">{routine.title}</div>
                                    <div className="text-sm text-muted-foreground">
                                      {routine.exercises.length} exercise{routine.exercises.length !== 1 ? 's' : ''}
                                    </div>
                                    <div className="text-xs text-muted-foreground mt-1">
                                      {routine.exercises.slice(0, 3).map(ex => ex.title).join(', ')}
                                      {routine.exercises.length > 3 && ` +${routine.exercises.length - 3} more`}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )
                    })}

                  {/* Other folder (routines without folders) */}
                  {organizedData.routinesWithoutFolder.length > 0 && (
                    <div>
                      {/* Other Folder Header */}
                      <div
                        onClick={() => toggleFolder(-1)}
                        className="flex items-center gap-2 p-3 rounded-lg border cursor-pointer hover:bg-muted/50 transition-colors"
                      >
                        {expandedFolders.has(-1) ? (
                          <FolderOpen className="h-4 w-4" />
                        ) : (
                          <Folder className="h-4 w-4" />
                        )}
                        <span className="text-muted-foreground font-medium">
                          Other
                        </span>
                        <span className="text-xs text-muted-foreground ml-auto">
                          {organizedData.routinesWithoutFolder.length} routine{organizedData.routinesWithoutFolder.length !== 1 ? 's' : ''}
                        </span>
                      </div>

                      {/* Other Folder Routines */}
                      {expandedFolders.has(-1) && (
                        <div className="ml-6 space-y-2 mt-2">
                          {organizedData.routinesWithoutFolder.map((routine) => (
                            <div
                              key={routine.id}
                              onClick={() => handleSelectRoutine(routine)}
                              className="flex items-center rounded-lg border p-3 hover:bg-muted/50 transition-colors cursor-pointer"
                            >
                              <div className="flex-1 min-w-0">
                                <div className="font-medium truncate">{routine.title}</div>
                                <div className="text-sm text-muted-foreground">
                                  {routine.exercises.length} exercise{routine.exercises.length !== 1 ? 's' : ''}
                                </div>
                                <div className="text-xs text-muted-foreground mt-1">
                                  {routine.exercises.slice(0, 3).map(ex => ex.title).join(', ')}
                                  {routine.exercises.length > 3 && ` +${routine.exercises.length - 3} more`}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </ModalContent>
    </Modal>
  )
}